import { z } from 'zod';
import { baseClaimSchema } from './baseClaim';
import { basePatientSchema } from './basePatient';

const DIGITS_8 = /^\d{8}$/;
const DIGITS_9 = /^\d{9}$/;
const DIGITS_10 = /^\d{10}$/;
const DIGITS_12 = /^\d{12}$/;
const ON_NUMBER = /^(\d{10}|\d{10}[A-Za-z]{2})$/;
const QC_NUMBER = /^[A-Za-z]{4}\d{8}$/;
const DND_NUMBER = /^[A-Za-z]\d{7}$/;
const IFHP_NUMBER = /^\d{8,10}$/;

const provinceEnum = z.enum([
  'MB',
  'ON',
  'BC',
  'AB',
  'QC',
  'SK',
  'NS',
  'NB',
  'NL',
  'PE',
  'YT',
  'NWT',
  'NT',
  'NU',
  'MSH',
  'QDOC',
  'DND',
  'IFHP',
]);

const optionalRegistrationSchema = z.string().optional().nullable().default('');
const qdocNumberSchema = z.string().trim().min(1, 'QDOC number is required');
const mbRegistrationSchema = z
  .string()
  .regex(
    /^[A-Za-z0-9]{6}$/,
    'MB registration must be exactly 6 alphanumeric characters'
  );

// Base schema shared across all provinces
export const baseHealthcardSchema = z.object({
  id: z.guid(),
  number: z.string(),
  registration: optionalRegistrationSchema,
  patientID: z.string(),
  patient: z.lazy(() => basePatientSchema).optional(),
  claims: z.lazy(() => baseClaimSchema.array().optional()),
  province: provinceEnum,
});

const createBaseSchema = z.object({
  number: z.string(),
  registration: optionalRegistrationSchema,
  patientID: z.string(),
  patient: z.lazy(() => basePatientSchema).optional(),
  claims: z.lazy(() => baseClaimSchema.array().optional()),
  province: provinceEnum,
});

export const healthcardSummarySchema = baseHealthcardSchema
  .pick({
    id: true,
    number: true,
    registration: true,
  })
  .strict();

// Specific schemas per province
const mbSchema = baseHealthcardSchema.extend({
  province: z.literal('MB'),
  number: z.string().regex(DIGITS_9, 'MB number must be exactly 9 digits'),
  registration: mbRegistrationSchema,
});

const onSchema = baseHealthcardSchema.extend({
  province: z.literal('ON'),
  number: z
    .string()
    .regex(ON_NUMBER, 'ON number must be 10 digits or 10 digits + 2 letters'),
  registration: optionalRegistrationSchema,
});

const bcSchema = baseHealthcardSchema.extend({
  province: z.literal('BC'),
  number: z.string().regex(DIGITS_10, 'BC number must be exactly 10 digits'),
  registration: optionalRegistrationSchema,
});

const abSchema = baseHealthcardSchema.extend({
  province: z.literal('AB'),
  number: z.string().regex(DIGITS_9, 'AB number must be exactly 9 digits'),
  registration: optionalRegistrationSchema,
});

const qcSchema = baseHealthcardSchema.extend({
  province: z.literal('QC'),
  number: z
    .string()
    .regex(QC_NUMBER, 'QC number must be 4 letters followed by 8 digits'),
  registration: optionalRegistrationSchema,
});

const skSchema = baseHealthcardSchema.extend({
  province: z.literal('SK'),
  number: z.string().regex(DIGITS_9, 'SK number must be exactly 9 digits'),
  registration: optionalRegistrationSchema,
});

const nsSchema = baseHealthcardSchema.extend({
  province: z.literal('NS'),
  number: z.string().regex(DIGITS_10, 'NS number must be exactly 10 digits'),
  registration: optionalRegistrationSchema,
});

const nbSchema = baseHealthcardSchema.extend({
  province: z.literal('NB'),
  number: z.string().regex(DIGITS_9, 'NB number must be exactly 9 digits'),
  registration: optionalRegistrationSchema,
});

const mshSchema = baseHealthcardSchema.extend({
  province: z.literal('MSH'),
  number: z
    .string()
    .regex(/^(\d{10}|MGP\d{10})$/, 'MSH requires 10 digits or MGP + 10 digits'),
  registration: optionalRegistrationSchema,
});

const qdocSchema = baseHealthcardSchema.extend({
  province: z.literal('QDOC'),
  number: qdocNumberSchema,
  registration: optionalRegistrationSchema,
});

const nlSchema = baseHealthcardSchema.extend({
  province: z.literal('NL'),
  number: z.string().regex(DIGITS_12, 'NL number must be exactly 12 digits'),
  registration: optionalRegistrationSchema,
});

const peSchema = baseHealthcardSchema.extend({
  province: z.literal('PE'),
  number: z.string().regex(DIGITS_8, 'PE number must be exactly 8 digits'),
  registration: optionalRegistrationSchema,
});

const ytSchema = baseHealthcardSchema.extend({
  province: z.literal('YT'),
  number: z.string().regex(DIGITS_9, 'YT number must be exactly 9 digits'),
  registration: optionalRegistrationSchema,
});

const nwtSchema = baseHealthcardSchema.extend({
  province: z.enum(['NWT', 'NT']),
  number: z.string().regex(DIGITS_9, 'NT number must be exactly 9 digits'),
  registration: optionalRegistrationSchema,
});

const nuSchema = baseHealthcardSchema.extend({
  province: z.literal('NU'),
  number: z.string().regex(DIGITS_9, 'NU number must be exactly 9 digits'),
  registration: optionalRegistrationSchema,
});

const dndSchema = baseHealthcardSchema.extend({
  province: z.literal('DND'),
  number: z
    .string()
    .regex(
      DND_NUMBER,
      'Department of National Defence number must be 1 alphabetic character followed by 7 digits'
    ),
  registration: optionalRegistrationSchema,
});

const ifhpSchema = baseHealthcardSchema.extend({
  province: z.literal('IFHP'),
  number: z.string().regex(IFHP_NUMBER, 'IFHP number must be 8 - 10 digits'),
  registration: optionalRegistrationSchema,
});

const createMbSchema = createBaseSchema.extend({
  province: z.literal('MB'),
  number: z.string().regex(DIGITS_9, 'MB number must be exactly 9 digits'),
  registration: mbRegistrationSchema,
});

const createOnSchema = createBaseSchema.extend({
  province: z.literal('ON'),
  number: z
    .string()
    .regex(ON_NUMBER, 'ON number must be 10 digits or 10 digits + 2 letters'),
  registration: optionalRegistrationSchema,
});

const createBcSchema = createBaseSchema.extend({
  province: z.literal('BC'),
  number: z.string().regex(DIGITS_10, 'BC number must be exactly 10 digits'),
  registration: optionalRegistrationSchema,
});

const createAbSchema = createBaseSchema.extend({
  province: z.literal('AB'),
  number: z.string().regex(DIGITS_9, 'AB number must be exactly 9 digits'),
  registration: optionalRegistrationSchema,
});

const createQcSchema = createBaseSchema.extend({
  province: z.literal('QC'),
  number: z
    .string()
    .regex(QC_NUMBER, 'QC number must be 4 letters followed by 8 digits'),
  registration: optionalRegistrationSchema,
});

const createSkSchema = createBaseSchema.extend({
  province: z.literal('SK'),
  number: z.string().regex(DIGITS_9, 'SK number must be exactly 9 digits'),
  registration: optionalRegistrationSchema,
});

const createNsSchema = createBaseSchema.extend({
  province: z.literal('NS'),
  number: z.string().regex(DIGITS_10, 'NS number must be exactly 10 digits'),
  registration: optionalRegistrationSchema,
});

const createNbSchema = createBaseSchema.extend({
  province: z.literal('NB'),
  number: z.string().regex(DIGITS_9, 'NB number must be exactly 9 digits'),
  registration: optionalRegistrationSchema,
});

const createMshSchema = createBaseSchema.extend({
  province: z.literal('MSH'),
  number: z
    .string()
    .regex(/^(\d{10}|MGP\d{10})$/, 'MSH requires 10 digits or MGP + 10 digits'),
  registration: optionalRegistrationSchema,
});

const createQdocSchema = createBaseSchema.extend({
  province: z.literal('QDOC'),
  number: qdocNumberSchema,
  registration: optionalRegistrationSchema,
});

const createNlSchema = createBaseSchema.extend({
  province: z.literal('NL'),
  number: z.string().regex(DIGITS_12, 'NL number must be exactly 12 digits'),
  registration: optionalRegistrationSchema,
});

const createPeSchema = createBaseSchema.extend({
  province: z.literal('PE'),
  number: z.string().regex(DIGITS_8, 'PE number must be exactly 8 digits'),
  registration: optionalRegistrationSchema,
});

const createYtSchema = createBaseSchema.extend({
  province: z.literal('YT'),
  number: z.string().regex(DIGITS_9, 'YT number must be exactly 9 digits'),
  registration: optionalRegistrationSchema,
});

const createNtSchema = createBaseSchema.extend({
  province: z.enum(['NWT', 'NT']),
  number: z.string().regex(DIGITS_9, 'NT number must be exactly 9 digits'),
  registration: optionalRegistrationSchema,
});

const createNuSchema = createBaseSchema.extend({
  province: z.literal('NU'),
  number: z.string().regex(DIGITS_9, 'NU number must be exactly 9 digits'),
  registration: optionalRegistrationSchema,
});

const createDndSchema = createBaseSchema.extend({
  province: z.literal('DND'),
  number: z
    .string()
    .regex(
      DND_NUMBER,
      'Department of National Defence number must be 1 alphabetic character followed by 7 digits'
    ),
  registration: optionalRegistrationSchema,
});

const createIfhpSchema = createBaseSchema.extend({
  province: z.literal('IFHP'),
  number: z.string().regex(IFHP_NUMBER, 'IFHP number must be 8 - 10 digits'),
  registration: optionalRegistrationSchema,
});

// Discriminated Union based on "province"
export const healthcardSchema = z.discriminatedUnion('province', [
  mbSchema,
  onSchema,
  abSchema,
  qcSchema,
  skSchema,
  nsSchema,
  nbSchema,
  mshSchema,
  qdocSchema,
  nlSchema,
  peSchema,
  ytSchema,
  nwtSchema,
  nuSchema,
  bcSchema,
  dndSchema,
  ifhpSchema,
]);

export type createHealthcard = z.infer<typeof createHealthcardSchema>;

export const createHealthcardSchema = z.discriminatedUnion('province', [
  createMbSchema,
  createOnSchema,
  createAbSchema,
  createQcSchema,
  createSkSchema,
  createNsSchema,
  createNbSchema,
  createMshSchema,
  createQdocSchema,
  createNlSchema,
  createPeSchema,
  createYtSchema,
  createNtSchema,
  createNuSchema,
  createBcSchema,
  createDndSchema,
  createIfhpSchema,
]);

// OCR checks identity independently of optional billing registration details.
export const healthcardIdentitySchema = z
  .object({
    province: provinceEnum,
    number: z.string(),
  })
  .superRefine((identity, context) => {
    const rule = healthcardSchema.options.find(
      (option) => option.shape.province.safeParse(identity.province).success
    );
    if (!rule?.shape.number.safeParse(identity.number).success) {
      context.addIssue({
        code: 'custom',
        path: ['number'],
        message: 'Invalid provincial healthcard number',
      });
    }
  });
